import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { TabView, TabPanel } from 'primereact/tabview';
import { Galleria } from 'primereact/galleria';
import { Carousel } from 'primereact/carousel';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { EcommerceProductoService } from '../services/EcommerceProductoService';
import { ECOMMERCE_CONFIG } from '../../config/EcommerceConfig';
import { getImageUrl } from '../utils/imageUtils';
import ProductCard from '../components/ProductCard';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();
    const { toggleFavorite, isFavorite } = useFavorites();
    const [quantity, setQuantity] = useState(1);
    const [product, setProduct] = useState(null);
    const [relatedProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const cargarProducto = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // El ID puede venir como id o idProducto del backend
                const productoId = id;
                
                const data = await EcommerceProductoService.obtenerProductoDetalle(
                    ECOMMERCE_CONFIG.EMPRESA_ID,
                    productoId
                );
                
                if (data) {
                    setProduct(data);
                } else {
                    setError('Producto no encontrado');
                }
            } catch (err) {
                setError('Error al cargar el producto');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            cargarProducto();
        }
    }, [id]);

    if (loading) {
        return (
            <div className="ec-section" style={{ textAlign: 'center', padding: '80px 0' }}>
                <div className="ec-container">
                    <ProgressSpinner />
                </div>
            </div>
        );
    }

    if (!product || error) {
        return (
            <div className="ec-section" style={{ textAlign: 'center', padding: '80px 0' }}>
                <div className="ec-container">
                    <i className="pi pi-exclamation-circle" style={{ fontSize: '4rem', color: 'var(--ec-text-light)', marginBottom: 16 }}></i>
                    <h2 style={{ color: 'var(--ec-text)', marginBottom: 8 }}>{error || 'Producto no encontrado'}</h2>
                    <p style={{ color: 'var(--ec-text-secondary)', marginBottom: 24 }}>
                        El producto que buscas no existe o fue removido.
                    </p>
                    <Button
                        label="Volver a la tienda"
                        icon="pi pi-arrow-left"
                        className="ec-product-addcart"
                        style={{ width: 'auto', padding: '12px 32px' }}
                        onClick={() => navigate('/tienda')}
                    />
                </div>
            </div>
        );
    }

    const productId = product.id || product.idProducto;
    const fav = isFavorite(productId);

    const handleAddToCart = () => {
        addToCart(product, quantity);
    };

    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating || 0);
        for (let i = 0; i < 5; i++) {
            stars.push(
                <i
                    key={i}
                    className={i < fullStars ? 'pi pi-star-fill' : 'pi pi-star'}
                    style={{ color: i < fullStars ? '#FFBF00' : 'var(--ec-border)', fontSize: '1rem' }}
                ></i>
            );
        }
        return stars;
    };

    // Manejar imágenes: puede ser array o string
    const getGalleryImages = () => {
        if (!product) return [];
        
        let images = [];
        if (Array.isArray(product.images)) {
            images = product.images;
        } else if (product.imagen) {
            images = [product.imagen];
        } else if (product.images) {
            images = [product.images];
        } else {
            images = ['https://via.placeholder.com/500x500?text=Sin+Imagen'];
        }
        
        return images.map(img => {
            const imageUrl = getImageUrl(img);
            return {
                itemImageSrc: imageUrl,
                thumbnailImageSrc: imageUrl,
                alt: product.nombre || product.name || 'Producto'
            };
        });
    };

    const galleryImages = getGalleryImages();

    const itemTemplate = (item) => (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
            <img
                src={item.itemImageSrc}
                alt={item.alt}
                style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }}
            />
        </div>
    );

    const thumbnailTemplate = (item) => (
        <img
            src={item.thumbnailImageSrc}
            alt={item.alt}
            style={{ width: 60, height: 60, objectFit: 'contain', borderRadius: 8 }}
        />
    );

    const productResponsiveOptions = [
        { breakpoint: '1024px', numVisible: 3, numScroll: 1 },
        { breakpoint: '768px', numVisible: 2, numScroll: 1 },
        { breakpoint: '560px', numVisible: 1, numScroll: 1 }
    ];

    const relatedProductTemplate = (p) => (
        <div style={{ padding: '0 10px' }}>
            <ProductCard product={p} />
        </div>
    );

    return (
        <div className="ec-detail">
            <div className="ec-container">
                {/* Breadcrumb */}
                <div className="ec-detail-breadcrumb" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.88rem', color: 'var(--ec-text-secondary)' }}>
                    <span style={{ cursor: 'pointer', color: 'var(--ec-primary)' }} onClick={() => navigate('/tienda')}>
                        Inicio
                    </span>
                    <i className="pi pi-angle-right" style={{ fontSize: '0.75rem' }}></i>
                    <span style={{ cursor: 'pointer', color: 'var(--ec-primary)' }} onClick={() => navigate('/tienda/catalogo')}>
                        Catálogo
                    </span>
                    <i className="pi pi-angle-right" style={{ fontSize: '0.75rem' }}></i>
                    <span>{product.nombre || product.name}</span>
                </div>

                {/* Product Grid */}
                <div className="ec-detail-grid ec-animate-fade">
                    {/* Gallery */}
                    <div className="ec-detail-gallery">
                        <Galleria
                            value={galleryImages}
                            numVisible={4}
                            item={itemTemplate}
                            thumbnail={thumbnailTemplate}
                            showThumbnails={galleryImages.length > 1}
                            showIndicators={false}
                            showItemNavigators={galleryImages.length > 1}
                            circular
                        />
                    </div>

                    {/* Info */}
                    <div className="ec-detail-info">
                        <span className="ec-detail-brand">{product.marca || product.brand || 'Marca'}</span>
                        <h1 className="ec-detail-name">{product.nombre || product.name}</h1>

                        {/* Rating */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ display: 'flex', gap: 2 }}>
                                {renderStars(product.rating || 4.8)}
                            </div>
                            <span style={{ fontWeight: 600, color: 'var(--ec-text)' }}>{product.rating || 4.8}</span>
                            <span style={{ color: 'var(--ec-text-secondary)', fontSize: '0.88rem' }}>
                                ({product.reviewCount || 0} opiniones)
                            </span>
                        </div>

                        {/* Pricing */}
                        <div className="ec-detail-pricing">
                            <span className="ec-detail-price">${(product.precioVenta || product.price || 0).toFixed(2)}</span>
                            {product.precioLista && product.precioLista > (product.precioVenta || product.price || 0) && (
                                <span className="ec-detail-oldprice">${product.precioLista.toFixed(2)}</span>
                            )}
                            {product.descuento > 0 && (
                                <span className="ec-detail-discount-badge">-{product.descuento}%</span>
                            )}
                        </div>

                        {product.precioLista && product.precioLista > (product.precioVenta || product.price || 0) && (
                            <span style={{ color: 'var(--ec-success)', fontWeight: 600, fontSize: '0.9rem' }}>
                                <i className="pi pi-check-circle" style={{ marginRight: 6 }}></i>
                                Ahorras ${(product.precioLista - (product.precioVenta || product.price || 0)).toFixed(2)}
                            </span>
                        )}

                        <p className="ec-detail-description">{product.descripcion || product.description || 'Sin descripción disponible'}</p>

                        {/* Stock */}
                        <div className={`ec-detail-stock ${(product.stock || 0) > 10 ? 'in-stock' : 'low-stock'}`}>
                            <i className="pi pi-circle-fill" style={{ fontSize: '0.5rem' }}></i>
                            {(product.stock || 0) > 10 ? (
                                <span><strong>En Stock</strong> — Disponible para envío inmediato</span>
                            ) : (
                                <span><strong>¡Últimas {(product.stock || 0)} unidades!</strong> — Ordena pronto</span>
                            )}
                        </div>

                        {/* Tags */}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Tag value="Envío Gratis" icon="pi pi-truck" severity="success" rounded />
                            <Tag value="Garantía 1 año" icon="pi pi-shield" rounded />
                            {product.isNew && <Tag value="Nuevo" icon="pi pi-star" severity="warning" rounded />}
                        </div>

                        {/* Quantity + Add to Cart */}
                        <div className="ec-detail-actions" style={{ marginTop: 16 }}>
                            <InputNumber
                                value={quantity}
                                onValueChange={(e) => setQuantity(e.value)}
                                showButtons
                                buttonLayout="horizontal"
                                min={1}
                                max={product.stock || 1}
                                decrementButtonClassName="p-button-outlined"
                                incrementButtonClassName="p-button-outlined"
                                incrementButtonIcon="pi pi-plus"
                                decrementButtonIcon="pi pi-minus"
                                style={{ width: 130 }}
                            />
                            <Button
                                label="Agregar al carrito"
                                icon="pi pi-shopping-cart"
                                className="ec-detail-addcart"
                                onClick={handleAddToCart}
                            />
                            <Button
                                icon={fav ? 'pi pi-heart-fill' : 'pi pi-heart'}
                                className={`ec-detail-fav ${fav ? 'is-fav' : ''}`}
                                onClick={() => toggleFavorite(product)}
                            />
                        </div>
                    </div>
                </div>

                {/* Tabs: Description, Specs, Reviews */}
                <div className="ec-detail-tabs">
                    <TabView>
                        <TabPanel header="Descripción" leftIcon="pi pi-info-circle mr-2">
                            <div style={{ padding: '16px 0', color: 'var(--ec-text-secondary)', lineHeight: 1.8 }}>
                                <p style={{ fontSize: '0.95rem' }}>{product.descripcion || product.description || 'Sin descripción'}</p>
                                {product.detalles && (
                                    <>
                                        <h4 style={{ color: 'var(--ec-text)', marginTop: 24, marginBottom: 12 }}>
                                            Características destacadas
                                        </h4>
                                        <ul style={{ paddingLeft: 20, fontSize: '0.9rem' }}>
                                            {product.detalles.slice(0, 4).map((detalle, idx) => (
                                                <li key={idx} style={{ marginBottom: 8 }}>
                                                    {detalle}
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </div>
                        </TabPanel>

                        <TabPanel header="Especificaciones" leftIcon="pi pi-list mr-2">
                            <div style={{ padding: '16px 0' }}>
                                {product.detalles ? (
                                    <table className="ec-detail-specs-table">
                                        <tbody>
                                            {product.detalles.map((detalle, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ fontWeight: 600, width: '40%' }}>Detalle {idx + 1}</td>
                                                    <td>{detalle}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p style={{ color: 'var(--ec-text-secondary)' }}>No hay especificaciones disponibles</p>
                                )}
                            </div>
                        </TabPanel>

                        <TabPanel header={`Opiniones (${product.reviewCount || 0})`} leftIcon="pi pi-comments mr-2">
                            <div style={{ padding: '24px 0', textAlign: 'center' }}>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 12 }}>
                                    {renderStars(product.rating || 4.8)}
                                </div>
                                <h3 style={{ color: 'var(--ec-text)', marginBottom: 8 }}>{product.rating || 4.8} de 5</h3>
                                <p style={{ color: 'var(--ec-text-secondary)', marginBottom: 24 }}>
                                    Basado en {product.reviewCount || 0} opiniones de clientes verificados
                                </p>

                                {/* Review distribution */}
                                {[5, 4, 3, 2, 1].map(star => {
                                    const percent = star === 5 ? 68 : star === 4 ? 20 : star === 3 ? 8 : star === 2 ? 3 : 1;
                                    return (
                                        <div key={star} style={{
                                            display: 'flex', alignItems: 'center', gap: 12,
                                            maxWidth: 400, margin: '0 auto', marginBottom: 8
                                        }}>
                                            <span style={{ width: 20, fontWeight: 600, fontSize: '0.88rem' }}>{star}</span>
                                            <i className="pi pi-star-fill" style={{ color: '#FFBF00', fontSize: '0.8rem' }}></i>
                                            <div style={{
                                                flex: 1, height: 8, background: 'var(--ec-border)',
                                                borderRadius: 4, overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    width: `${percent}%`, height: '100%',
                                                    background: '#FFBF00', borderRadius: 4,
                                                    transition: 'width 0.6s ease'
                                                }}></div>
                                            </div>
                                            <span style={{ width: 36, fontSize: '0.82rem', color: 'var(--ec-text-secondary)' }}>
                                                {percent}%
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </TabPanel>
                    </TabView>
                </div>

                {/* Related Products */}
                {relatedProducts.length > 0 && (
                    <div style={{ marginTop: 48 }}>
                        <div className="ec-section-header">
                            <h2 className="ec-section-title">
                                Productos <span>Relacionados</span>
                            </h2>
                        </div>
                        <Carousel
                            value={relatedProducts}
                            itemTemplate={relatedProductTemplate}
                            numVisible={4}
                            numScroll={1}
                            responsiveOptions={productResponsiveOptions}
                            className="ec-product-carousel"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductDetail;
